import {FC, PropsWithChildren, useLayoutEffect} from "react";
import {useEnvironment} from "@common-core/react-runtime/context";
import {Environment} from "../runtime";
import {useSearchParams} from "../hooks";

export interface IdsWebComponentsLoaderProps {
  baseUrl?: string;
}

export const IdsWebComponentsLoader: FC<
  PropsWithChildren<IdsWebComponentsLoaderProps>
> = ({baseUrl = "https://cdn.interstate.coxautoinc.com", children}) => {
  const params = useSearchParams();
  const {interstateVersion: supportedVersion} = useEnvironment<Environment>();

  useLayoutEffect(() => {
    const requestedVersion = params.get("ids-version") || supportedVersion;
    console.info(`Loading IDS ${requestedVersion} ...`);

    const script = window.document.createElement("script");
    script.id = "ids-web-components";
    script.setAttribute("data-testid", "ids-web-components");
    script.setAttribute("data-version", requestedVersion);
    script.type = "module";
    script.defer = true;
    script.src = `${baseUrl}/${requestedVersion}/web-components/index.mjs`;
    script.onload = () => {
      console.info(`IDS ${requestedVersion} loaded successfully`);
      window.dispatchEvent(
        new CustomEvent("ids-web-components-loaded", {
          detail: {supportedVersion, requestedVersion, fallback: false}
        })
      );
    };
    script.onerror = () => {
      console.warn(`Failed to load IDS ${requestedVersion}`);
      if (requestedVersion !== supportedVersion) {
        console.warn(`Falling back to ${supportedVersion}`);
        const fallbackScript = window.document.createElement("script");
        fallbackScript.id = "ids-web-components";
        fallbackScript.setAttribute("data-testid", "ids-web-components");
        fallbackScript.setAttribute("data-version", supportedVersion);
        fallbackScript.type = "module";
        fallbackScript.defer = true;
        fallbackScript.src = `${baseUrl}/${supportedVersion}/web-components/index.mjs`;
        fallbackScript.onload = () => {
          console.info(`IDS ${supportedVersion} fallback loaded successfully`);
          window.dispatchEvent(
            new CustomEvent("ids-web-components-loaded", {
              detail: {supportedVersion, requestedVersion, fallback: true}
            })
          );
        };
        fallbackScript.onerror = () => {
          if (fallbackScript.parentNode) {
            fallbackScript.parentNode.removeChild(fallbackScript);
          }
          window.dispatchEvent(
            new CustomEvent("ids-web-components-failed", {
              detail: {supportedVersion, requestedVersion, fallback: true}
            })
          );
        };
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        window.document.head.appendChild(fallbackScript);
      } else {
        window.dispatchEvent(
          new CustomEvent("ids-web-components-failed", {
            detail: {supportedVersion, requestedVersion, fallback: false}
          })
        );
      }
    };
    window.document.head.appendChild(script);

    return () => {
      const injectedScript =
        window.document.getElementById("ids-web-components");
      if (injectedScript && injectedScript.parentNode) {
        injectedScript.parentNode.removeChild(injectedScript);
      }
    };
  }, [baseUrl, params, supportedVersion]);

  return children;
};
